/**
 * メニューを更新する際 /web_app/apps/stailer-external-admin/components/header.tsx
 * にある外部管理画面v2のメニューも同時に更新してください。
 */

import { EcSite } from '@apis/type/ec_site_pb'
import config, { Environment } from 'config'
import { type LDFlagSet } from 'launchdarkly-js-client-sdk'
import { getPartnerConfig } from '~/models/partner_config'
import { UserRole } from '~/types/user'

type Notification = {
  name: string
}

export type Feature = {
  title: string
  urlPath: string
  description?: string
  notification?: Notification
}

export type FeatureSection = {
  title: string
  items: Feature[]
}

const removeDisabledFeatureSections = (
  sections: Array<FeatureSection | null>,
): FeatureSection[] => {
  return sections.flatMap((e) => (e && e.items.length ? e : []))
}

const getFeatureFlags = (siteType: EcSite.SiteType) => {
  return getPartnerConfig(siteType).featureFlags
}

/** 全店舗が配信対象であることを表すワイルドカード */
const POINT_CAMPAIGN_ALL_SHOPS_WILDCARD = '*'

/**
 * LaunchDarkly のフラグ stailer-external-admin-point-campaign-enabled-shop-ids から
 * siteType の配信対象店舗IDリストを取り出す。
 *
 * フラグ値は siteType 名をキー、配信対象に指定できる店舗IDの配列を値に持つ
 * JSON (e.g. `{"AEONRETAIL": ["1234"]}`)。キーが存在する siteType で機能が有効になり
 * その配列が配信対象店舗ID。`["*"]` は全店舗が配信対象であることを表す。
 * キーが存在しない (機能無効) 場合は null を返す。
 * 外部管理画面v2 (web_app) の usePointCampaignFlag と同じ判定。
 */
const getPointCampaignTargetableShopIds = (
  ldFlags: LDFlagSet | undefined,
  siteType: EcSite.SiteType,
): string[] | null => {
  const value: unknown = ldFlags?.['stailer-external-admin-point-campaign-enabled-shop-ids']
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  // $getSiteTypeName と同じ方法で enum 値から siteType 名を引く
  const siteTypeName = Object.keys(EcSite.SiteType)[siteType]
  const shopIds = (value as Record<string, unknown>)[siteTypeName]
  if (!Array.isArray(shopIds)) {
    return null
  }
  return shopIds.filter((shopId): shopId is string => typeof shopId === 'string')
}

/**
 * ログインアカウントがポイントキャンペーンの導線にアクセスできるか。
 * バックエンドRPCのゲート・外部管理画面v2と同じ適格性ルール。
 *
 * - 担当店舗 (shopId) 未設定: 全店舗を運用する管理者 (admin / developer) のみ可。
 *   店舗スコープで運用する manager / member は不可
 * - 担当店舗あり: ロールに関わらず、配信対象が `"*"` を含む、または担当店舗を含むなら可
 *
 * 前提として機能が siteType で有効 (LDフラグにキーが存在) であること。
 * 機能無効の siteType では常に false。
 */
export const isPointCampaignAccessibleForAccount = (
  ldFlags: LDFlagSet | undefined,
  siteType: EcSite.SiteType,
  role: UserRole,
  shopId: string | null,
): boolean => {
  const targetableShopIds = getPointCampaignTargetableShopIds(ldFlags, siteType)
  if (targetableShopIds === null) {
    return false
  }
  if (shopId === null || shopId === '') {
    return isAdmin(role)
  }
  return (
    targetableShopIds.includes(POINT_CAMPAIGN_ALL_SHOPS_WILDCARD) ||
    targetableShopIds.includes(shopId)
  )
}

type FeatureSectionFunction = (siteType: EcSite.SiteType, role: UserRole) => FeatureSection | null

const getOrder: FeatureSectionFunction = (siteType, role) => {
  const featureFlags = getFeatureFlags(siteType)
  return {
    title: '注文',
    items: [
      {
        title: '注文一覧',
        urlPath: '/orders',
        description: 'お客様からの注文の一覧を表示、管理します。',
      },
      ...(featureFlags.enableStailerTMS
        ? [
            {
              title: 'ピッキング伝票',
              urlPath: '/picking_list',
              description: 'ピッキング伝票を印刷できます。',
            },
            {
              title: '配達・お渡し',
              urlPath: '/delivery_cars',
              description:
                '本日付の注文を注文枠別に表示します。お客様にお渡しする明細を印刷できます。',
            },
          ]
        : []),
      ...(featureFlags.enableDeliveryVouchers && isAdmin(role)
        ? [
            {
              title: '配送表・納品書',
              urlPath: '/delivery_vouchers',
              description: '配送指示書、各種配送表、納品書を印刷できます。',
            },
          ]
        : []),

      {
        title: '注文商品検索',
        urlPath: '/orders/products',
        description: '本日付の注文の商品を検索します。商品のステータスを確認できます。',
      },

      ...(!isProduction()
        ? [
            {
              title: 'デバッグ機能（開発環境のみ）',
              urlPath: '/v2/debug',
              description: 'テストのための機能を提供します。',
            },
          ]
        : []),
    ],
  }
}

const getProduct: FeatureSectionFunction = (siteType, role) => {
  const featureFlags = getFeatureFlags(siteType)
  return {
    title: '商品',
    items: [
      {
        title: '商品一覧',
        urlPath: '/products',
        description: '売場に掲載する商品を管理します。',
      },
      ...(isAdminOrManager(role)
        ? [
            {
              title: '商品カテゴリ',
              urlPath: '/v2/ec_site_categories',
              description: '売場に表示される商品カテゴリの階層を確認できます。',
            },
          ]
        : []),
      ...(featureFlags.enableCatalog && isAdmin(role)
        ? [
            {
              title: 'カタログ一覧',
              urlPath: '/catalogs',
              description: 'カタログの管理、追加、編集を行えます。',
            },
          ]
        : []),
      ...(featureFlags.enableRecurringOrder && isAdmin(role)
        ? [
            {
              title: '定期便一覧',
              urlPath: '/recurring_order_settings/users',
              description: '定期便の登録、編集、削除を行えます。',
            },
          ]
        : []),
    ],
  }
}

const getContact: FeatureSectionFunction = (siteType, role) => {
  const featureFlags = getFeatureFlags(siteType)
  return {
    title: 'お客様関連',
    items: [
      ...(!isManager(role)
        ? [
            {
              title: 'お客様検索',
              urlPath: '/users',
              description: 'お客様を検索します。',
            },
          ]
        : []),
      ...(featureFlags.enableContants
        ? [
            {
              title: 'お問い合わせ',
              urlPath: '/v2/contacts',
              description: 'お客様からのお問い合わせを表示します。',
            },
          ]
        : []),

      ...(featureFlags.enableSubscriptionV2 && isAdmin(role)
        ? [
            {
              title: '有料サービス',
              urlPath: '/subscription_users',
              description: 'お客様の有料サービスの利用状況を確認、変更できます。',
            },
          ]
        : []),
      ...(featureFlags.enableOutgoingCallHistory && isAdmin(role)
        ? [
            {
              title: '発信履歴',
              urlPath: '/v2/outgoing_call_histories',
              description: 'スタッフからお客様への発信一覧を表示します。',
            },
          ]
        : []),
      ...(featureFlags.enableCompensationClame && isAdmin(role)
        ? [
            {
              title: '補償手続き',
              urlPath: '/v2/compensations',
              description: '売上修正、返金対応などを行えます。',
            },
          ]
        : []),
    ],
  }
}

const getBankAccount: FeatureSectionFunction = (siteType, role) => {
  return getFeatureFlags(siteType).enableBankAccountTransfer && isAdmin(role)
    ? {
        title: '口座振替関連',
        items: [
          {
            title: '口座振替管理',
            urlPath: '/bank_accounts/',
            description: 'お客様への口座振替に関する情報の一覧を表示、管理します。',
          },
          {
            title: '請求データ管理',
            urlPath: '/account_transfer/requests',
            description: 'お客様の口座振替に関する請求データの一覧を表示、管理します。',
          },
          {
            title: '請求書・明細書',
            urlPath: '/account_transfer/statements',
            description: 'お客様の口座振替に関する請求書・明細書を表示、管理します。',
          },
        ],
      }
    : null
}

const getAnnouncement: FeatureSectionFunction = (_, role) => {
  return isAdminOrManager(role)
    ? {
        title: 'お知らせ',
        items: [
          {
            title: 'お知らせ',
            urlPath: '/announcements',
            description: 'お客様へのお知らせを管理します。',
          },
          {
            title: 'プッシュ通知',
            urlPath: '/batch_push_notification_reservations',
            description: 'アプリに配信するプッシュ通知を管理します。',
          },
        ],
      }
    : null
}

const getMarketing = (
  siteType: EcSite.SiteType,
  role: UserRole,
  ldFlags?: LDFlagSet,
  shopId: string | null = null,
): FeatureSection | null => {
  const featureFlags = getFeatureFlags(siteType)
  return {
    title: '販促関連',
    items: [
      ...(isAdminOrManager(role)
        ? [
            {
              title: '掲載バナー',
              urlPath: '/banners',
              description: '売場に掲載するバナーを管理します。',
            },
          ]
        : []),
      ...(featureFlags.showBundleMixmatch
        ? [
            {
              title: 'まとめ割・よりどり割一覧',
              urlPath: '/bundle_rules',
              description: 'まとめ割・よりどり割の商品を管理します。',
            },
          ]
        : []),
      ...(isAdminOrManager(role)
        ? [
            {
              title: 'クーポン一覧',
              urlPath: '/coupons',
              description: 'クーポンを管理します。',
            },
          ]
        : []),
      ...(isAdmin(role)
        ? [
            {
              title: '特集一覧',
              urlPath: '/features',
              description: '特集を管理します。',
            },
          ]
        : []),
      ...(featureFlags.enableReservationProduct && isAdmin(role)
        ? [
            {
              title: '予約特集一覧',
              urlPath: '/reservation_products',
              description: '予約特集を管理します。',
            },
          ]
        : []),
      ...(featureFlags.enablePointCampaign &&
      isPointCampaignAccessibleForAccount(ldFlags, siteType, role, shopId)
        ? [
            {
              title: 'ポイントキャンペーン',
              urlPath: '/v2/point_campaigns',
              description: 'ポイントキャンペーンを管理します。',
            },
          ]
        : []),
    ],
  }
}

const getAnalytics: FeatureSectionFunction = (_, role) => {
  return {
    title: 'アナリティクス',
    items: [
      ...(isAdmin(role)
        ? [
            {
              title: '事業指標',
              urlPath: '/v2/analytics',
              description: '事業全体の成果を示す指標の集計結果が確認できます。',
            },
          ]
        : []),
      ...(isMember(role)
        ? [
            {
              title: '事業指標',
              urlPath: '/v2/analytics/demand',
              description: '事業全体の成果を示す指標の集計結果が確認できます。',
            },
          ]
        : []),
      ...(isAdmin(role) || isMember(role)
        ? [
            {
              title: 'エンゲージメント指標',
              urlPath: '/v2/analytics/order_access_user',
              description: 'ユーザーの利用状況を示す指標の集計結果が確認できます。',
            },
          ]
        : []),
      ...(isAdmin(role)
        ? [
            {
              title: '販促指標',
              urlPath: '/v2/analytics/coupon',
              description: '販促の成果を示す指標の集計結果が確認できます。',
            },
          ]
        : []),
    ],
  }
}

const getSupport: FeatureSectionFunction = (_) => {
  return {
    title: 'サポート',
    items: [
      {
        title: 'ご利用ガイド',
        urlPath: 'https://stailer.zendesk.com/hc/ja',
        description: 'スタッフアプリ・管理画面に関するご利用ガイドです。',
      },
      {
        title: '不具合報告・お問い合わせ',
        urlPath: '/v2/bug_reports/new',
        description: '管理画面に関する不具合報告・お問い合わせフォームです。',
      },
    ],
  }
}

const getBaseSetting: FeatureSectionFunction = (_, role) => {
  return {
    title: '基本設定',
    items: [
      {
        title: 'アカウント設定',
        urlPath: '/accountSettings',
      },
      ...(isAdmin(role)
        ? [
            {
              title: 'ユーザーアカウント一覧',
              urlPath: '/v2/external_admin_users',
            },
            {
              title: '監査ログ',
              urlPath: '/v2/audit_logs',
            },
          ]
        : []),
    ],
  }
}

const getMasterData: FeatureSectionFunction = (_, role) => {
  return {
    title: 'マスタデータ設定',
    items: [
      ...(isAdmin(role)
        ? [
            {
              title: 'マスタデータ',
              urlPath: '/master_datas',
            },
          ]
        : []),
      ...(isAdminOrManager(role)
        ? [
            {
              title: '商品画像一括更新',
              urlPath: '/upload_product_images',
            },
          ]
        : []),
    ],
  }
}

const getDeliverySetting: FeatureSectionFunction = (_, role) => {
  return {
    title: '注文枠設定',
    items: [
      ...(isAdmin(role)
        ? [
            {
              title: '注文枠ひな形設定',
              urlPath: '/delivery_time_setting',
            },
          ]
        : []),
      ...(isAdminOrManager(role)
        ? [
            {
              title: '注文枠一覧',
              urlPath: '/delivery_times',
            },
          ]
        : []),
    ],
  }
}

const getShop: FeatureSectionFunction = (siteType, role) => {
  return {
    title: '店舗設定',
    items: [
      ...(isAdminOrManager(role)
        ? [
            {
              title: '店舗情報',
              urlPath: '/ec_site_pickup_shops',
            },
          ]
        : []),
      ...[
        {
          title: '配達エリア',
          urlPath: '/v2/delivery_areas',
        },
      ],
      ...(isAdminOrManager(role)
        ? [
            {
              title: '配達エリア移管',
              urlPath: '/delivery_area_migrations',
            },
          ]
        : []),
      ...(isAdmin(role)
        ? [
            ...(siteType !== EcSite.SiteType.FRESTA
              ? [
                  {
                    title: '配達エリアグループ',
                    urlPath: '/delivery_area_groups',
                  },
                ]
              : []),
          ]
        : []),

      ...(isAdmin(role)
        ? [
            {
              title: '店舗グループ設定',
              urlPath: '/v2/shop_groups',
            },
          ]
        : []),
      ...(isAdminOrManager(role)
        ? [
            {
              title: 'ドキュメント一覧',
              urlPath: '/v2/documents',
            },
          ]
        : []),
    ],
  }
}

export const operationFeatureTitle = 'オペレーション設定' as const

const getOperationFeatures: FeatureSectionFunction = (siteType, role) => {
  const featureFlags = getFeatureFlags(siteType)
  return {
    title: operationFeatureTitle,
    items: [
      ...(isAdmin(role)
        ? [
            {
              title: '商品配置',
              urlPath: '/fulfillment/product_arrangement',
            },
          ]
        : []),
      ...(isAdmin(role) && featureFlags.isDssAllowed
        ? [
            {
              title: 'DSS接続設定',
              urlPath: '/v2/dss_connection_settings',
            },
          ]
        : []),
      ...(isAdmin(role) && featureFlags.enableProzAnswers
        ? [
            {
              title: 'Proz Answers接続設定',
              urlPath: '/v2/proz_answers_connection_settings',
            },
          ]
        : []),
    ],
  }
}

const getDataExport: FeatureSectionFunction = (_siteType, _role) => {
  return {
    title: 'データ出力',
    items: [
      {
        title: 'データダウンロード',
        urlPath: '/v2/data_download',
        description: '特定の条件で絞り込んだデータをダウンロードできます。',
      },
    ],
  }
}

const getProcurement: FeatureSectionFunction = (siteType, role) => {
  return getFeatureFlags(siteType).enableProcurement && isAdmin(role)
    ? {
        title: '仕入れ関連',
        items: [
          {
            title: '仕入先一覧',
            urlPath: '/suppliers',
            description: '仕入先の管理と新規作成が行なえます。',
          },
          {
            title: '入荷予定',
            urlPath: '/stock_arrivals',
            description: '入荷予定商品の確認が行なえます。',
          },
          {
            title: '仕入れマスタダウンロード',
            urlPath: '/download_procurement_masters',
            description: '販売計画書や需要予測データをダウンロードできます。',
          },
        ],
      }
    : null
}

const getPayment: FeatureSectionFunction = (siteType, role) => {
  const featureFlags = getFeatureFlags(siteType)
  return {
    title: '売上・取引関連',
    items: [
      ...(featureFlags.enableStailerStandardSalesFinalizer
        ? [
            {
              title: '売上確定',
              urlPath: '/stailer_standard_finalize_sale',
              description: '受け取りが完了した注文の売上確定処理を行い、POSに売上を連携します。',
            },
          ]
        : []),

      ...(isAdmin(role)
        ? [
            {
              title: '決済ステータス管理',
              urlPath: '/payment_issues',
              description: '決済が失敗した注文の確認、手動処理を行えます。',
              notification: {
                name: 'payment_issues',
              },
            },
            {
              title: '取引一覧',
              urlPath: '/transaction_records',
              description: '完了した取引の一覧を表示します。',
            },
          ]
        : []),
    ],
  }
}

// ナビゲーションバーのメインメニュー
export const getNavbarMainFeatures = (
  siteType: EcSite.SiteType,
  role: UserRole,
  ldFlags?: LDFlagSet,
  shopId: string | null = null,
): FeatureSection[] => {
  return removeDisabledFeatureSections([
    getOrder(siteType, role),
    getProduct(siteType, role),
    getContact(siteType, role),
    getPayment(siteType, role),
    getAnnouncement(siteType, role),
    getMarketing(siteType, role, ldFlags, shopId),
    getAnalytics(siteType, role),
    getDataExport(siteType, role),
    getSupport(siteType, role),
  ])
}

// ナビゲーションバーの右端に隠れているメニュー
export const getNavbarOtherFeatures = (
  siteType: EcSite.SiteType,
  role: UserRole,
): FeatureSection[] => {
  return removeDisabledFeatureSections([
    getBaseSetting(siteType, role),
    getMasterData(siteType, role),
    getDeliverySetting(siteType, role),
    getShop(siteType, role),
    getOperationFeatures(siteType, role),
  ])
}

// Dashboardのメインメニュー
export const getMainFeatures = (
  siteType: EcSite.SiteType,
  role: UserRole,
  ldFlags?: LDFlagSet,
  shopId: string | null = null,
): FeatureSection[] => {
  return removeDisabledFeatureSections([
    getOrder(siteType, role),
    getProduct(siteType, role),
    getContact(siteType, role),
    getPayment(siteType, role),
    getAnnouncement(siteType, role),
    getMarketing(siteType, role, ldFlags, shopId),
    getAnalytics(siteType, role),
    getDataExport(siteType, role),
    getSupport(siteType, role),
    getBankAccount(siteType, role),
    getProcurement(siteType, role),
  ])
}

// Dashboardの設定・その他の左側
export const getLeftSubFeatures = (siteType: EcSite.SiteType, role: UserRole): FeatureSection[] => {
  return removeDisabledFeatureSections([
    getBaseSetting(siteType, role),
    getMasterData(siteType, role),
    getDeliverySetting(siteType, role),
  ])
}

// Dashboardの設定・その他の右側
export const getRightSubFeatures = (
  siteType: EcSite.SiteType,
  role: UserRole,
): FeatureSection[] => {
  return removeDisabledFeatureSections([
    getShop(siteType, role),
    getOperationFeatures(siteType, role),
  ])
}

export const isAdmin = (role: UserRole): boolean => {
  // Developerはすべての機能を確認する想定であるため、Adminと同じページにアクセスできるようにする。
  // フロントエンドは基本的に同等だが、バックエンドでは招待の実行が拒否される点が異なる。
  return role === UserRole.Admin || role === UserRole.Developer
}
export const isManager = (role: UserRole): boolean => {
  return role === UserRole.Manager
}
export const isAdminOrManager = (role: UserRole): boolean => {
  return isAdmin(role) || isManager(role)
}
export const isMember = (role: UserRole): boolean => {
  return role === UserRole.Member
}

const isProduction = (): boolean => {
  return config.environment === Environment.production
}
